import { getPlanConfigurationConstraints } from '@wix/ambassador-pricing-plans-v3-plan/http';
import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { uoUFunnelPlanCustomisationPageSrc92Evid115 } from '@wix/bi-logger-pricing-plans-data/v2';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { PlanCustomizationPublicData } from '@wix/pricing-plans-router-utils';
import { allowsFutureStartDateV3 } from '@wix/pricing-plans-utils';
import type { CreateControllerFn, ControllerParams, ControllerFlowAPI, FlowAPI } from '@wix/yoshi-flow-editor';
import { DEMO_FORM_ID, plansFixtureTranslated } from '../../fixtures';
import { EcomClient } from '../../services/ecom-client';
import { resolveLocale } from '../../utils';
import { toError } from '../../utils/errors';
import { PlanCustomizationInteraction } from './fedops';
import { planV2ToDemoPlanV3 } from './mappers/plan-v2-to-demo-plan-v3';
import { FormsService } from './services/forms';
import { PlanCustomization } from './types';
import { calculateDateIntervalOverlap, calculateInitialStartDate } from './utils/date-constraints';
import { RootProductPageProps } from './Widget';

const createController: CreateControllerFn = async ({ flowAPI }: ControllerParams) => {
  function reportInteractionStarted(name: PlanCustomizationInteraction) {
    flowAPI.panoramaClient.transaction(name).start();
  }

  function reportInteractionFinished(name: PlanCustomizationInteraction) {
    flowAPI.panoramaClient.transaction(name).finish();
  }

  return {
    async pageReady() {
      reportInteractionStarted(PlanCustomizationInteraction.Initialize);
      const wixCodeApi = flowAPI.controllerConfig.wixCodeApi;
      const setProps: (props: Partial<RootProductPageProps>) => void = flowAPI.controllerConfig.setProps;
      const formsService = new FormsService(flowAPI);

      setProps({
        status: 'PAGE_LOADING',
        formControllerStatus: 'LOADING',
        locale: resolveLocale(wixCodeApi),
        proceedToPayment,
        clearStatusMessage: () => setProps({ statusMessage: undefined }),

        // @ts-expect-error Undocumented viewer prop, which adds "height: auto" to root TPA section
        fitToContentHeight: true,
      });

      reportInteractionStarted(PlanCustomizationInteraction.GetProductPageData);
      const routerData = await getProductPageData(flowAPI);
      reportInteractionFinished(PlanCustomizationInteraction.GetProductPageData);

      logCustomizationPageEvent({
        actionGroup: 'load',
        actionName: 'start_load',
      });
      const {
        plan,
        customizationData: { integrationData, checkoutFlowId },
      } = routerData;
      const ecomClient = new EcomClient({
        wixCodeApi,
        flowAPI,
        widgetType: 'customization',
      });

      const isStartDateFunctionsEnabled = flowAPI.experiments.enabled(TPA_EXPERIMENTS.START_DATE_FUNCTIONS);
      const allowFutureStartDate = allowsFutureStartDateV3(plan);
      const verticalMinDate = integrationData.minStartDate ?? undefined;
      const verticalMaxDate = integrationData.maxStartDate ?? undefined;

      let apiMinDate: string | undefined;
      let apiMaxDate: string | undefined;
      let startDateLimitsMessage: string | undefined;

      if (isStartDateFunctionsEnabled && allowFutureStartDate) {
        try {
          const constraintsResponse = await flowAPI.httpClient.request(
            getPlanConfigurationConstraints({
              planId: plan.id!,
              pricingVariantId: plan.pricingVariants?.[0]?.id!,
            }),
          );
          const constraints = constraintsResponse.data;
          apiMinDate = constraints.startDateLimits?.earliestDate ?? undefined;
          apiMaxDate = constraints.startDateLimits?.latestDate ?? undefined;
          startDateLimitsMessage = constraints.startDateLimits?.errorMessage ?? undefined;
        } catch (error) {
          apiMinDate = undefined;
          apiMaxDate = undefined;
          startDateLimitsMessage = undefined;
        }
      }

      const { minDate, maxDate, hasOverlap } = calculateDateIntervalOverlap(
        verticalMinDate,
        verticalMaxDate,
        apiMinDate,
        apiMaxDate,
      );

      const finalMinStartDate = minDate;
      const finalMaxStartDate = maxDate;
      const initialStartDate = calculateInitialStartDate(finalMinStartDate, finalMaxStartDate);
      const disableAllDates = !hasOverlap || !initialStartDate;

      reportInteractionStarted(PlanCustomizationInteraction.InitializeFormsController);
      formsService
        .maybeInitFormController(plan)
        .then(() => {
          reportInteractionFinished(PlanCustomizationInteraction.InitializeFormsController);
          setProps({ formControllerStatus: 'READY' });
        })
        .catch(() => {
          setProps({ formControllerStatus: 'ERROR' });
        });

      setProps({
        status: 'PAGE_READY',
        plan,
        minStartDate: finalMinStartDate,
        maxStartDate: finalMaxStartDate,
        initialStartDate,
        disableAllDates,
        noAvailableDatesMessage: startDateLimitsMessage,
      });
      logCustomizationPageEvent({
        actionGroup: 'load',
        actionName: 'finish_load',
      });

      reportInteractionFinished(PlanCustomizationInteraction.Initialize);

      async function proceedToPayment({ formValues, startDate }: PlanCustomization) {
        reportInteractionStarted(PlanCustomizationInteraction.ProceedToPayment);
        logCustomizationPageEvent({
          actionGroup: 'click',
          actionName: 'continue',
        });
        setProps({ status: 'CHECKOUT_LOADING', statusMessage: undefined });
        const submissionId = await formsService.maybeSubmitForm(plan.formId, formValues).catch((error) => {
          flowAPI.panoramaClient
            .errorMonitor()
            .reportError(toError(error), { tags: { interactionTag: 'proceed_to_payment' } });
          return null;
        });

        try {
          const validStartDate = isStartDateValid(startDate) ? startDate : undefined;
          await ecomClient.createAndNavigateToCheckout({
            plan,
            submissionId,
            startDate: validStartDate,
            integrationData,
            checkoutFlowId,
          });
          reportInteractionFinished(PlanCustomizationInteraction.ProceedToPayment);
        } catch (error) {
          const resolvedError = flowAPI.errorHandler.getResolvedError(error);
          setProps({ status: 'ERROR', statusMessage: resolvedError.message });
          logCustomizationPageEvent({
            actionGroup: 'error',
            actionName: resolvedError.applicationError?.code ?? '',
          });
        }
      }

      function logCustomizationPageEvent(params: { actionGroup: 'load' | 'click' | 'error'; actionName: string }) {
        if (!routerData) {
          return flowAPI.bi?.report(
            uoUFunnelPlanCustomisationPageSrc92Evid115({
              ...params,
            }),
          );
        }

        return flowAPI.bi?.report(
          uoUFunnelPlanCustomisationPageSrc92Evid115({
            isCustomStartDateEnabled: Boolean(allowsFutureStartDateV3(routerData.plan)).toString(),
            isCustomCheckoutFormEnabled: Boolean(routerData.plan.formId).toString(),
            checkoutFlowId: routerData.customizationData.checkoutFlowId,
            referralinfo: routerData.customizationData.integrationData.biOptions?.referralInfo,
            planGuid: routerData.plan.id!,
            ...params,
          }),
        );
      }
    },
    async updateConfig() {},
  };
};

export default createController;

async function getProductPageData(flowAPI: ControllerFlowAPI): Promise<PlanCustomizationPublicData> {
  const routerData = flowAPI.controllerConfig.wixCodeApi.window.getRouterPublicData<PlanCustomizationPublicData>();
  if (!routerData) {
    if (flowAPI.environment.isEditor || flowAPI.environment.isPreview) {
      const demoPlan = getDemoPlanV3(flowAPI.translations.t);
      return {
        plan: {
          ...demoPlan,
          currency: flowAPI.controllerConfig.wixCodeApi.site.currency,
        },
        customizationData: {
          integrationData: {},
          planId: demoPlan.id!,
          checkoutFlowId: undefined,
        },
      };
    }

    // TODO: Add fallback client calls to get the data
    throw new Error('Router data is missing');
  }

  return routerData;
}

function getDemoPlanV3(t: FlowAPI['translations']['t']): Plan {
  const [planV2] = plansFixtureTranslated(t);
  return planV2ToDemoPlanV3({
    ...planV2,
    formId: DEMO_FORM_ID,
    allowFutureStartDate: true,
  });
}

function isStartDateValid(dateString: string | null): boolean {
  if (!dateString) {
    return false;
  }

  const date = new Date(dateString);
  const today = new Date();
  if (date < today) {
    return false;
  }

  if (date.getDate() === today.getDate()) {
    return false;
  }

  return true;
}
